import { XMarkIcon } from '@heroicons/react/24/outline';
import { Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useSubscriptionManagerContext } from '../../../SubscriptionManager.context';
import { useCancelSubscriptionContext } from './CancelSubscription.context';
import CancelForm from './CancelSubscription.form';
import CancelSubscriptionProvider from './CancelSubscription.provider';
import ConfirmationModal from './ConfirmationModal/ConfirmationModal.component';
import RetentionModal from './RetentionModal/RetentionModal.component';
import RetentionOfferModal from './RetentionOfferModal/RetentionOfferModal.component';
import RetentionZeroLeadsModal from './RetentionZeroLeadsModal/RetentionZeroLeadsModal.component';
import RetentionShell from './RetentionShell.component';
import RetentionProgress from './RetentionProgress.component';
import SchedulingModal from './SchedulingModal/SchedulingModal.component';

export default function CancelSubscriptionModal() {
  const { showCancelSubscriptionModal } = useSubscriptionManagerContext();

  if (!showCancelSubscriptionModal) {
    return null;
  }

  return (
    <CancelSubscriptionProvider>
      <ModalContent />
    </CancelSubscriptionProvider>
  );
}

function ModalContent() {
  const { t } = useTranslation('settings/SubscriptionManager');
  const { showCancelSubscriptionModal } = useSubscriptionManagerContext();
  const {
    showForm,
    showOfferModal,
    showConfirmationModal,
    showSchedulingModal,
    hasZeroLeads,
    isLoadingLeads,
    isLoadingReasonSelection,
    handleKeepSubscription,
    handleContinue,
    handleClose,
    handleCloseConfirmation,
  } = useCancelSubscriptionContext();

  // Show scheduling modal
  if (showSchedulingModal) {
    return (
      <SchedulingModal
        isOpen={showCancelSubscriptionModal}
        onClose={handleCloseConfirmation}
      />
    );
  }

  // Show confirmation modal
  if (showConfirmationModal) {
    return (
      <ConfirmationModal
        isOpen={showCancelSubscriptionModal}
        onClose={handleCloseConfirmation}
      />
    );
  }

  // Show offer modal
  if (showOfferModal) {
    return (
      <RetentionOfferModal
        isOpen={showCancelSubscriptionModal}
        onClose={handleClose}
      />
    );
  }

  // Show form modal
  if (showForm) {
    return (
      <Modal
        open={showCancelSubscriptionModal}
        className="flex max-h-[92vh] w-11/12 max-w-[680px] flex-col overflow-hidden rounded-lg p-0"
      >
        {/* No title bar. The heading inside already names the step, and the bar cost
            the room the rows needed to breathe. */}
        <button
          type="button"
          className="btn btn-ghost btn-sm btn-circle absolute right-3 top-3 z-10"
          onClick={handleClose}
          aria-label={t('cancelSubscription.retention.close')}
          disabled={isLoadingReasonSelection}
        >
          <XMarkIcon aria-hidden="true" className="h-5 w-5" />
        </button>
        <RetentionProgress current={2} fill className="px-10 pr-16 pt-6" />
        <CancelForm />
      </Modal>
    );
  }

  // `hasZeroLeads` is forced false until the lead count lands, so while the request is
  // in flight neither opening is knowable. Rendering one anyway is what produced the
  // reported flash: the customer got the active-workspace copy ("mira tu actividad",
  // "Leads organizados") and then watched it become the empty-workspace copy a moment
  // later. Sharing a shell fixed the size jump but not that, because the words still
  // changed.
  //
  // So the shell holds the frame itself, with its own placeholders, and no variant is
  // chosen until there is an answer. One view that fills in, never two that trade.
  if (isLoadingLeads) {
    return (
      <RetentionShell
        isOpen={showCancelSubscriptionModal}
        isLoading
        isBusy
        onKeep={handleKeepSubscription}
        onContinue={handleContinue}
        onClose={handleClose}
      />
    );
  }

  if (hasZeroLeads) {
    return <RetentionZeroLeadsModal isOpen={showCancelSubscriptionModal} />;
  }

  return <RetentionModal isOpen={showCancelSubscriptionModal} />;
}
